import { forwardRef, React, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

import AccountInput from '../../../Component/AccountComponent/AccountInput.js';
import SignUpIdInput from '../../../Component/AccountComponent/SignUpIdInput.js';
import SignUpPwInput from '../../../Component/AccountComponent/SignUpPwInput.js';
import FetchModule from '../../Share/Network/FetchModule.js';
import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

const SignUpInfo1 = forwardRef((props, ref) => {
	const { t } = useLanguage();
	const { isDark } = useTheme();
	const theme = isDark ? darkTheme : lightTheme;
	const [ownerClickCount, setOwnerClickCount] = useState(0);
	const [idClickCount, setIdClickCount] = useState(0);
	const [pwClickCount, setPwClickCount] = useState(0);
	const [availableOfId, setAvailableOfId] = useState(false);
	const [certifiedId, setCertifiedId] = useState('');
	const [isBlankId, setIsBlankId] = useState(true);
	const [idPlanation, setIdPlanation] = useState(t('signUp.requiredField'));
	const [isClicked, setIsClicked] = useState(false); 
	const [isAnimate, setIsAnimate] = useState(false);

	const TopLevelRef = useRef();
	const IdRef = useRef();
	const PWRef = useRef();
	const OwnerRef = useRef();
	const OwnerPlanationRef = useRef();
	const IdPlanationRef = useRef();
	const PwPlanationRef = useRef();

	useImperativeHandle(ref, () => ({
		enableEnterAnimation: () => {
			setIsAnimate(true);
		},
		disableEnterAnimation: () => {
			setIsAnimate(false);
		},
		setOpacity: (val) => {
			TopLevelRef.current.style.opacity = val;
			if (val === 0) TopLevelRef.current.setAttribute('inert', '');
			else TopLevelRef.current.removeAttribute('inert');
		},
		setLocation: (val) => {
			TopLevelRef.current.style.transform = `translateX(${val}%)`;
		},
		getOwnerName: () => {
			return OwnerRef.current.getValue();
		},
		getUserId: () => {
			return IdRef.current.getValue();
		},
		getUserPw: () => {
			return PWRef.current.getValue();
		},
		getAvailableOfId: () => {
			return availableOfId;
		},
		getCertifiedId: () => {
			return certifiedId;
		},
		isBlankOwnerName: () => {
			OwnerRef.current.setDefaultPlaceholder(t('signUp.ownerNamePlaceholder'));
			OwnerRef.current.plusClickCount();
			OwnerPlanationRef.current.style.opacity = 1;
		},
		isBlankId: () => {
			setIsBlankId(true);
			IdRef.current.setDefaultPlaceholder(t('signUp.idPlaceholder'));
			IdRef.current.plusClickCount();
			IdPlanationRef.current.style.opacity = 1;
		},
		didNotCheckAvailableId: () => {
			setAvailableOfId(false);
			setIsBlankId(false);
			setIdPlanation(t('signUp.idCheckRequired'));
			IdPlanationRef.current.style.opacity = 1;
			IdPlanationRef.current.style.color = theme.pointRed;
		},
		isBlankPassword: () => {
			PWRef.current.setDefaultPlaceholder(t('signUp.pwPlaceholder'));
			PWRef.current.plusClickCount();
			PwPlanationRef.current.style.opacity = 1;
		},
		// 패스워드 유효성 검증 (에러 표시하고 boolean 반환)
		isValidPassword: () => {
			const isValid = PWRef.current.isValidPassword();

			if (!isValid) {
				PwPlanationRef.current.innerText = t('signUp.pwFormatError');
				PwPlanationRef.current.style.opacity = 1;
				PwPlanationRef.current.style.color = theme.pointRed;
			}
			else {
				PwPlanationRef.current.style.opacity = 0;
			}

			return isValid;
		}
	}));

	useEffect(() => {
		TopLevelRef.current.setAttribute('inert', '');
	}, []);

	useEffect(() => {
		if (idClickCount === 0 && pwClickCount === 0 && ownerClickCount === 0) return;

		if (IdRef.current.getValue() === '' && idClickCount > 0) {
			IdPlanationRef.current.style.color = theme.pointRed;
			IdPlanationRef.current.style.opacity = 1;
		}
		else {
			IdPlanationRef.current.style.opacity = 0;
		}
		
		// 비밀번호 유효성 검증 (실시간)
		const pw = PWRef.current.getValue();

		if (pwClickCount > 0) {
			if (pw === '') {
				PwPlanationRef.current.innerText = t('signUp.requiredField');
				PwPlanationRef.current.style.color = theme.pointRed;
				PwPlanationRef.current.style.opacity = 1;
			}
			else if (!isValidPassword(pw)) {
				PwPlanationRef.current.innerText = t('signUp.pwFormatError');
				PwPlanationRef.current.style.color = theme.pointRed;
				PwPlanationRef.current.style.opacity = 1;
			}
			else {
				PwPlanationRef.current.style.opacity = 0;
			}
		}

		if (OwnerRef.current.getValue() === '' && ownerClickCount > 0) {
			OwnerPlanationRef.current.style.color = theme.pointRed;
			OwnerPlanationRef.current.style.opacity = 1;
		}
		else {
			OwnerPlanationRef.current.style.opacity = 0;
		}
		
	}, [idClickCount, pwClickCount, ownerClickCount])

	// 비밀번호 유효성 검증 함수
	function isValidPassword(pw) {
		if (pw.length < 6) return false;
		if (!/[A-Za-z]/.test(pw)) return false;      // 영문
		if (!/[0-9]/.test(pw)) return false;         // 숫자
		if (!/[!@#$%^&*(),.?":{}|<>]/.test(pw)) return false; // 특수문자
		return true;
	}

	function clickCountFn(className, clickCount) {
		if (className === 'IdInput') setIdClickCount(clickCount);
		else if (className === 'PWInput') setPwClickCount(clickCount);
		else if (className === 'OwnerInput') setOwnerClickCount(clickCount);
	}

	// ID형식 확인 정규식
	function isAvailableId(asValue) {
		const regExp = /^[a-z]+[a-z0-9]{4,49}$/g;
 
		return regExp.test(asValue);
	}

	function duplicateCheck() {
		if (isClicked) return;

		const userId = IdRef.current.getValue();

		if (userId === '') {
			IdRef.current.plusClickCount();
			IdRef.current.setDefaultPlaceholder(t('signUp.idPlaceholder'));
			IdPlanationRef.current.style.color = theme.pointRed;
			IdPlanationRef.current.style.opacity = 1;

			return;
		}
		else if (!isAvailableId(userId)) {
			setIdPlanation(t('signUp.idFormatError'));
			IdPlanationRef.current.style.color = theme.pointRed;
			IdPlanationRef.current.style.opacity = 1;
			setIsClicked(false);

			return;
		}

		setIsBlankId(false);

		const fetchModule = new FetchModule();
		(
			async () => {
				const responseData = await fetchModule.postData('account/asp/inspection/' + userId, 'POST');

				if (responseData.status === 200) {
					setAvailableOfId(true);
					setCertifiedId(userId);
					setIsClicked(true);
					setIdPlanation(t('signUp.idAvailable'));
					IdPlanationRef.current.style.color = theme.primary;
					IdPlanationRef.current.style.opacity = 1;
					// 중복확인 완료 후 border를 회색으로 리셋
					IdRef.current.reset(t('signUp.idPlaceholder'));
				}
				else {
					setAvailableOfId(false);
					setIsClicked(false);

					if (isBlankId) {
						setIdPlanation(t('signUp.requiredField'));
						IdRef.current.setDefaultPlaceholder(t('signUp.requiredField'));
					}
					else {
						setIdPlanation(t('signUp.idUnavailable'));
						IdRef.current.setDefaultPlaceholder(t('signUp.idUnavailable'));
					}
					IdPlanationRef.current.style.color = theme.pointRed;
					IdPlanationRef.current.style.opacity = 1;
				}
			}
		)();
	}

	return (
		<TopLevelWrapper ref={TopLevelRef} $animate={isAnimate}>
			<InputInfo>{t('signUp.ownerName')}</InputInfo>
			<AccountInput ref={OwnerRef} className='OwnerInput' placeholder={t('signUp.ownerNamePlaceholder')} type='text' clickCountFn={clickCountFn}/>
			<AccountExplanation ref={OwnerPlanationRef}>{t('signUp.requiredField')}</AccountExplanation>
			<InputInfo>{t('signUp.id')}</InputInfo>
			<SignUpIdInput ref={IdRef} isClicked={isClicked} setIsClicked={setIsClicked} className='IdInput' placeholder={t('signUp.idPlaceholder')} type='text' clickCountFn={clickCountFn} clickFn={duplicateCheck} text={t('signUp.duplicateCheck')}/>
			<AccountExplanation ref={IdPlanationRef}>{idPlanation}</AccountExplanation>
			<InputInfo>{t('signUp.password')}</InputInfo>
			<SignUpPwInput ref={PWRef} className='PWInput' placeholder={t('signUp.pwPlaceholder')} type='password' clickCountFn={clickCountFn} />
			<AccountExplanation ref={PwPlanationRef}>{t('signUp.requiredField')}</AccountExplanation>
		</TopLevelWrapper>
	)
})

export default SignUpInfo1;

const startAnimation = keyframes`
	0% { opacity: 0; transform: translateX(150%); }
	10% { opacity: 0.1; transform: translateX(130%); }
	20% { opacity: 0.2; transform: translateX(100%); }
	30% { opacity: 0.3; transform: translateX(70%); }
	40% { opacity: 0.4; transform: translateX(40%); }
	50% { opacity: 0.5; transform: translateX(20%); }
	60% { opacity: 0.6; transform: translateX(0%); }
	70% { opacity: 0.7;}
	80% { opacity: 0.8; }
	90% { opacity: 0.9;}
	100% { opacity: 1;}
`;


const TopLevelWrapper = styled.div`
	position: absolute;
	display: flex;
	
	flex-direction: column; 
	justify-content: flex-start;

	width: 468px;
	// width: max(271px, 24.375vw);
	// width: max(250px, 24.375vw);
	// height: 281px;

	margin-top: 145px;
	// margin-top: max(106px, 8.28125vw);

	background-color: ${props => props.theme.subBackground};

	opacity: 0;
	z-index: 1;
	transform: translateX(150%);

	animation: none;

	${(props) =>
		props.$animate &&
		css`
			animation: ${startAnimation} 0.5s ease-out;
	`}

	transition: all 0.3s ease-in-out;
`;

const InputInfo = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 16px;
	// font-size: max(12px, 0.7291666666666667vw);
	// line-height: 20px;
	
	margin: 19px 0 0 0;
	// margin-top: max(11.73px, 0.9895833333333333vw);
	// margin-right: 0px;
	// margin-bottom: 0px;
	// margin-left: 0px;

	cursor: default;

	color: ${props => props.theme.text};
`;

const AccountExplanation = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;
	// font-size: max(12px, 0.625vw);
	// line-height: 17.38px;

	// color: ${props => props.bold ? `#FF3131` : `#33C8FF`};
	color: ${props => props.theme.pointRed};

	margin: 4px 0 0 0;

	opacity: 0;

  transition: all 0.5s ease-in-out;
`;

